/**
 * src/game/weapons/types.ts — 主武器系統 (R-005) 引擎核心型別 + 純邏輯。
 *
 * ── 鐵則放寬(boss 2026-06-13 拍板,見 docs/spec/WEAPON_SYSTEM_SPEC.md §0)──
 *   武器脫離「學習=戰力」。武器威力由**武器等級/稀有度**決定,**不讀修煉值**、
 *   **不需學習解鎖**。武器行為層(此資料夾)**絕不**呼叫學習側戰力寫入函式
 *   (addFactionXP / addFreeXP / allocateFreeXP 那三支 — learning-game-separation.test.ts
 *   仍綠);只讀 weaponLevels 算威力、
 *   只寫單場 scene 狀態(子彈池/敵人 hp)。
 *
 * ── 設計:WeaponBehavior 引擎 ──
 *   每把武器自己擁有「開火迴圈」。scene 的 auto-fire tick 委派給
 *   `equippedWeapon.tick(ctx)`,武器自己決定「何時 spawn 什麼 / 怎麼打」。
 *   ctx 把 scene 能力(spawn helper / 敵人查詢 / RunModifiers / 武器等級)包成函式
 *   傳入,**複用既有 _spawnOneBullet / 子彈池 / 敵人集合**,不重造任何系統。
 *
 *   為可單測,純邏輯(蓄力進度、追蹤轉向、近戰命中)抽成下方 pure 函式;
 *   Phaser 互動留在 scene adapter(behaviors 只透過 ctx 的函式 seam 操作)。
 */

import type { RunModifiers } from '../systems/run-modifiers';

/** 武器引擎能查到的「敵人」最小形狀(scene HEnemy / boss 都能餵)。 */
export interface WeaponTargetInfo {
  x: number;
  y: number;
  /** 施加傷害(scene 包成函式;behaviors 不直接動 hp)。 */
  takeDamage: (amount: number) => void;
  /** 是否還活著(死掉的不選為目標、不再命中)。 */
  alive: boolean;
}

/**
 * 每幀傳給 `WeaponBehavior.tick` 的 context。scene 在 adapter 建立後傳入。
 * 全部能力以函式 seam 暴露,讓 behaviors 可被 headless 單測(餵假 ctx)。
 */
export interface WeaponTickContext {
  /** 本幀經過秒數(dtSec)。 */
  dt: number;
  /** 目前 wall-clock 毫秒(scene.time.now)。 */
  nowMs: number;
  /** 玩家(坦克)中心 x。 */
  playerX: number;
  /** 玩家(坦克)中心 y。 */
  playerY: number;
  /** 本場單場旗標(複用既有 RunModifiers,武器讀來疊散彈/穿透等)。 */
  mods: RunModifiers;
  /** 此武器目前等級(weaponLevels[id],預設 1)。威力曲線用,不讀修煉值。 */
  weaponLevel: number;
  /**
   * 直射一發子彈:複用 scene 既有 `_spawnOneBullet(dmg, vy, sizeMult)`。
   * 武器只決定「射什麼」,子彈池/碰撞仍走既有管線(保真關鍵)。
   */
  spawnBullet: (dmg: number, vy: number, sizeMult: number) => void;
  /**
   * 基礎子彈傷害(scene 的 `_effectiveBulletDamage(nowMs)`,含門派/升級加成)。
   * 武器以此為基底乘武器倍率,讓既有升級對所有武器一致生效。
   */
  baseBulletDamage: () => number;
  /** 查當前所有「可被武器命中/追蹤」的敵人(含 boss,scene 包好)。 */
  enemies: () => WeaponTargetInfo[];
  /**
   * 放一道貫穿整條 lane 的光束(railgun 用):scene 對 yBand 內全敵人施傷 + 畫 FX。
   * behaviors 只算「何時放、放在哪條 y、多少傷」;渲染留 scene。
   */
  fireBeam?: (centerY: number, halfHeight: number, damage: number) => void;
  /** 近戰旋刃的視覺角度更新(scene 旋轉刀刃 sprite);headless 時可缺省。 */
  setBladeAngle?: (angleRad: number) => void;
  /** 蓄力視覺進度更新(railgun 蓄力條/光點);0..1。headless 時可缺省。 */
  setChargeRatio?: (ratio: number) => void;
  /**
   * 放一道「自動鎖定持續雷射光束」這一幀(laser-beam 用,boss 2026-06-17 重新設計):
   * scene 取「第 beamIndex 近的前方敵人」當瞄準方向,從砲口往該方向射一道光束,對沿線
   * halfHeight 內全敵人施(低)傷 + 畫指向性光束 FX。beamIndex 0 = 最近敵人(主光束),
   * 1,2… = 次近(進化後多道光束分別鎖不同敵人)。與 fireBeam(軌道砲橫向蓄力一發)徹底
   * 不同:每幀呼叫、傷害低、過熱關閉、會瞄準。damage=0 = 過節流幀只畫光束不施傷。
   * behaviors 只算「這幀放第幾道、多粗、多少傷」;選目標/渲染/施傷留 scene。headless 可缺省。
   */
  fireLaserAimed?: (beamIndex: number, halfHeight: number, damage: number) => void;
  /**
   * 落一發榴彈(mortar 用):scene 在落點 (landingX, landingY) 半徑 radius 內全敵人施
   * AoE 傷害 + 畫拋物軌跡與爆炸 FX。behaviors 只算「落哪、半徑多大、多少傷」;渲染留
   * scene。headless 時可缺省。
   */
  fireMortar?: (landingX: number, landingY: number, radius: number, damage: number) => void;
  /**
   * 雷射過熱視覺進度更新(0..1;1 = 全熱)。overheated=true 表示這幀因過熱關閉、
   * scene 可畫紅色過熱條提示「正在冷卻」。headless 時可缺省。
   */
  setHeatRatio?: (ratio: number, overheated: boolean) => void;
}

/**
 * 一把主武器的行為。每把擁有自己的開火迴圈狀態(蓄力/冷卻/角度)。
 * - `tick`:每幀依 ctx 推進內部狀態並在適當時機 spawn / 施傷。
 * - `reset`:run 開始歸零內部狀態(跨場不殘留;同 RunModifiers 精神)。
 */
export interface WeaponBehavior {
  /** 穩定 id(= progress.selectedWeapon / weaponLevels key / registry key)。 */
  readonly id: string;
  tick(ctx: WeaponTickContext): void;
  reset(): void;
}

/** 武器等級威力曲線:Lv1 = ×1,每級 +12%(線性),不讀修煉值。 */
export function weaponLevelDamageMult(level: number): number {
  const lv = Number.isFinite(level) && level >= 1 ? Math.floor(level) : 1;
  return 1 + (lv - 1) * 0.12;
}

// ── 穿透/廣命中傷害衰減(boss 2026-06-14「不能打得又廣傷害又高」)──────────────
//
// 設計原則:「廣(穿透/掃射/多目標)」與「高單發傷害」二擇一,不可兼得。
// 一發穿透彈/一條額外光束打到的「第 N 個目標」傷害要遞減,讓「廣」不再等於「全場滿傷」。
//
// 兩個 falloff 旋鈕(都 data-driven 預設,可被 balance config 覆寫):
//   - PIERCE_DAMAGE_FALLOFF:同一發子彈/飛彈穿過第 1、2、3… 個敵人的逐級衰減。
//   - LANE_DAMAGE_FALLOFF:雷射/軌道炮/榴彈的「額外 lane / 額外 beam / 子爆」逐條衰減。
// 兩者都用「幾何遞減」:第 0 個(主目標)= 滿傷,第 k 個 = base × falloff^k。

/** 穿透彈逐級傷害衰減(第 N 個被穿目標 = base × 此值^N)。0.55 = 穿越快速遞減。 */
export const PIERCE_DAMAGE_FALLOFF = 0.55;

/** 額外 lane / beam / 子爆逐條傷害衰減(第 N 條 = base × 此值^N)。0.6 = 廣但不滿傷。 */
export const LANE_DAMAGE_FALLOFF = 0.6;

/**
 * 幾何傷害衰減:第 `hitIndex` 個目標(0 = 主目標滿傷)拿到 `baseDamage × falloff^hitIndex`。
 * 純函式;穿透/額外 lane/子爆 共用。回傳「至少 1」(向下取整但不歸零,避免穿透後段 0 傷
 * 看起來像漏打)。非法輸入退化為 baseDamage(不衰減)。
 *
 * @param baseDamage  主目標(第 0 個)的滿傷。
 * @param hitIndex    這是第幾個被打到的目標(0-based;0=滿傷,1=第二個,以此類推)。
 * @param falloff     每多一個目標的衰減倍率(0..1;1=不衰減)。
 */
export function pierceDamageFalloff(baseDamage: number, hitIndex: number, falloff: number): number {
  if (!Number.isFinite(baseDamage) || baseDamage <= 0) return 0;
  const idx = Number.isFinite(hitIndex) && hitIndex > 0 ? Math.floor(hitIndex) : 0;
  if (idx === 0) return Math.round(baseDamage);
  const f = Number.isFinite(falloff) && falloff > 0 && falloff <= 1 ? falloff : 1;
  return Math.max(1, Math.round(baseDamage * Math.pow(f, idx)));
}

// ── 三選一升級 → 各武器詮釋(同卡不同效果,boss 2026-06-14)──────────────────────
//
// 問題:平衡 pass 後只有 rapid-cannon 真的吃 spread/autoFire/pierce/bounce/blast
// (它委派回 scene 完整子彈管線)。其它 5 把(railgun/laser/homing/spin/mortar)
// 自走開火迴圈,沒讀這些 mods → 升級對它們無效。
//
// 解法:把「同一張升級卡」對「每把武器」的詮釋抽成下面這些**純函式**(可單測),
// 各 behavior 讀 ctx.mods(單場,RunModifiers)再套到自己的機制上 —— 每張卡對每把
// 都有感,但「感覺」依武器不同(boss 要的差異)。
//
// 鐵則:
//   - 射速一律用 ctx.mods.effectiveAutoFireMult()(下限 clamp,疊到上限不爆)。
//   - 多顆一律用 ctx.mods.effectiveSpreadBullets()(上限 cap,疊到上限不爆)。
//   - 穿透/彈跳/爆裂/巨彈讀 raw 欄位(pierceCount / bounceCount+evoBounceBonus /
//     spreadBlastRadius / bulletSizeMult);這些本就無上限存取器(同 scene 用法)。
//   - 全部純單場機制,不寫修煉值。

/**
 * 「攻擊增速」詮釋:把武器自己的「節奏秒數/間隔」乘上 effectiveAutoFireMult。
 * autoFireMult < 1 = 更快,所以這裡直接相乘:蓄力更快、間隔更短、tick 更密。
 * 已 clamp(下限 0.4)的 mult 傳入,疊到上限也只會快到 ~2.5×,不會爆。
 * @param baseSec 武器原本的節奏秒數(蓄力秒/發射間隔/tick 間隔…,單位隨呼叫端)。
 * @param effAutoFireMult ctx.mods.effectiveAutoFireMult()(已 clamp)。
 */
export function applyRateMod(baseSec: number, effAutoFireMult: number): number {
  const m = Number.isFinite(effAutoFireMult) && effAutoFireMult > 0 ? effAutoFireMult : 1;
  return baseSec * m;
}

/**
 * 「多顆子彈」詮釋(非彈幕武器用):把 effectiveSpreadBullets 顆數換成「範圍/條數
 * 加成倍率」。每多一顆 → 範圍 +`perBullet`(預設 +25%),讓「光束加寬/多 lane/
 * 多一片刃」這類詮釋有感但受 cap 約束(疊到上限只到 +cap×perBullet)。
 * @param extraBullets ctx.mods.effectiveSpreadBullets()(已 cap)。
 * @param perBullet 每顆的範圍加成(預設 0.25)。
 */
export function spreadWidthMult(extraBullets: number, perBullet = 0.25): number {
  const n = Number.isFinite(extraBullets) && extraBullets > 0 ? Math.floor(extraBullets) : 0;
  return 1 + n * perBullet;
}

/**
 * 「巨彈/判定範圍」詮釋:把 bulletSizeMult 套到武器的「判定半徑/半高/環寬」。
 * 直接回傳一個安全的範圍倍率(NaN/<=0 退化為 1 = 不變)。
 * @param bulletSizeMult ctx.mods.bulletSizeMult。
 */
export function sizeRangeMult(bulletSizeMult: number): number {
  return Number.isFinite(bulletSizeMult) && bulletSizeMult > 0 ? bulletSizeMult : 1;
}

/**
 * 「爆裂(spreadBlastRadius)」詮釋:把散彈爆裂半徑換成「AoE/判定範圍的額外 px」。
 * mortar 直接加到爆炸半徑;其它武器加到判定半高/環寬。NaN/<0 → 0(無加成)。
 * @param spreadBlastRadius ctx.mods.spreadBlastRadius。
 * @param scale 套用比例(預設 1;非 AoE 武器可調小,避免判定過寬)。
 */
export function blastRangeBonus(spreadBlastRadius: number, scale = 1): number {
  const r = Number.isFinite(spreadBlastRadius) && spreadBlastRadius > 0 ? spreadBlastRadius : 0;
  return r * scale;
}

/**
 * 「穿透 + 彈跳」詮釋(無實體子彈的武器用):把 pierce/bounce 次數換成「額外效果
 * 觸發數」—— mortar 落地後再炸幾次(子爆)、railgun/laser 多幾條光束 lane。
 * 取 pierce 與 bounce 的「合計」(都代表「打更多目標」的精神),再 clamp 一個上限。
 * @param pierceLeft ctx.mods.pierceCount。
 * @param bounceCount ctx.mods.bounceCount + evoBounceBonus。
 * @param cap 額外觸發上限(避免疊太多;預設 3)。
 */
export function extraHitChains(pierceLeft: number, bounceCount: number, cap = 3): number {
  const p = Number.isFinite(pierceLeft) && pierceLeft > 0 ? Math.floor(pierceLeft) : 0;
  const b = Number.isFinite(bounceCount) && bounceCount > 0 ? Math.floor(bounceCount) : 0;
  return Math.min(cap, p + b);
}

// ── 蓄力(railgun)純邏輯 ─────────────────────────────────────────────────────

/** 蓄力狀態(純資料,可單測)。 */
export interface ChargeState {
  /** 已累積秒數。 */
  elapsedSec: number;
}

/**
 * 推進蓄力。回傳新 elapsed + 是否「這幀剛蓄滿要放」。
 * - 未滿:累加 dt,fired=false。
 * - 滿了(>= chargeSec):fired=true,elapsed 歸零(放完重新蓄)。
 * 純函式,不碰 scene;behaviors 用回傳值決定是否 fireBeam。
 */
export function advanceCharge(
  state: ChargeState,
  dtSec: number,
  chargeSec: number,
): { elapsedSec: number; fired: boolean } {
  const next = state.elapsedSec + Math.max(0, dtSec);
  if (next >= chargeSec) {
    return { elapsedSec: 0, fired: true };
  }
  return { elapsedSec: next, fired: false };
}

/** 蓄力進度比(0..1),給 UI 蓄力條。 */
export function chargeRatio(elapsedSec: number, chargeSec: number): number {
  if (chargeSec <= 0) return 1;
  const r = elapsedSec / chargeSec;
  return r < 0 ? 0 : r > 1 ? 1 : r;
}

// ── 追蹤飛彈(homing)純邏輯 ──────────────────────────────────────────────────

/** 2D 向量(px / px-per-sec)。 */
export interface Vec2 {
  x: number;
  y: number;
}

/**
 * 限速轉向:把速度向量 (vx,vy) 朝目標方向轉,每幀最多轉 `maxTurnRad` 弧度,
 * 速度大小維持 `speed`。turnRate 有限 → K-3 閃得過、飛彈不會瞬間鎖死。
 *
 * 純函式(無 scene);behaviors 每幀拿回新速度後自己 x+=vx*dt。
 * - 目標在身後也只會慢慢繞回來(受 maxTurnRad 限制),不瞬轉。
 */
export function steerToward(
  pos: Vec2,
  vel: Vec2,
  target: Vec2,
  speed: number,
  maxTurnRad: number,
): Vec2 {
  const desiredX = target.x - pos.x;
  const desiredY = target.y - pos.y;
  const desiredLen = Math.hypot(desiredX, desiredY);
  // 已到目標上(距離 ~0):維持現速度方向,避免除 0。
  if (desiredLen < 1e-6) return { x: vel.x, y: vel.y };
  const curAngle = Math.atan2(vel.y, vel.x);
  const desiredAngle = Math.atan2(desiredY, desiredX);
  // 夾到 [-π, π] 的最短角差,再 clamp 到 ±maxTurnRad。
  let delta = desiredAngle - curAngle;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  const clamped = Math.max(-maxTurnRad, Math.min(maxTurnRad, delta));
  const newAngle = curAngle + clamped;
  return { x: Math.cos(newAngle) * speed, y: Math.sin(newAngle) * speed };
}

/**
 * 從一群目標中挑「最近的活目標」(homing 用)。無活目標回 null。
 * 純函式;scene 把 enemies() 結果餵進來。
 */
export function nearestTarget(
  from: Vec2,
  targets: readonly WeaponTargetInfo[],
): WeaponTargetInfo | null {
  let best: WeaponTargetInfo | null = null;
  let bestD2 = Infinity;
  for (const t of targets) {
    if (!t.alive) continue;
    const dx = t.x - from.x;
    const dy = t.y - from.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = t;
    }
  }
  return best;
}

// ── 旋刃(spin-blade)近戰純邏輯 ──────────────────────────────────────────────

/**
 * 近戰環繞刀刃命中判定:刀刃繞玩家半徑 `orbitRadius` 旋轉,任何活敵人「中心到
 * 玩家距離」落在 [orbitRadius - band, orbitRadius + band] 環帶內即命中。
 * 回傳本幀被命中的目標 index 清單(scene 據此施持續傷害)。
 *
 * 純函式;K-3 友善:用「環帶」而非單點刀尖,接觸到就吃傷,不用對準角度。
 */
export function bladeHitIndices(
  player: Vec2,
  targets: readonly WeaponTargetInfo[],
  orbitRadius: number,
  band: number,
): number[] {
  const hits: number[] = [];
  const inner = Math.max(0, orbitRadius - band);
  const outer = orbitRadius + band;
  for (let i = 0; i < targets.length; i++) {
    const t = targets[i];
    if (!t.alive) continue;
    const d = Math.hypot(t.x - player.x, t.y - player.y);
    if (d >= inner && d <= outer) hits.push(i);
  }
  return hits;
}

// ── 雷射光束(laser-beam)過熱純邏輯 ──────────────────────────────────────────

/** 雷射熱量狀態(純資料,可單測)。 */
export interface HeatState {
  /** 目前熱量(0..maxHeat)。 */
  heat: number;
  /** 是否處於過熱冷卻中(熱量見頂後鎖住,降到 resumeHeat 才解鎖)。 */
  overheated: boolean;
}

/** 雷射過熱曲線參數(秒/單位熱)。可由武器等級調(等級高 → 散熱快、上限高)。 */
export interface HeatParams {
  /** 熱量上限,到頂即過熱。 */
  maxHeat: number;
  /** 開火時每秒升溫。 */
  heatPerSec: number;
  /** 不開火(或過熱中)時每秒降溫。 */
  coolPerSec: number;
  /** 過熱後熱量降到此值以下才解除過熱(滯後,避免抖動)。 */
  resumeHeat: number;
}

/**
 * 推進雷射熱量一幀。回傳新狀態 + 這幀「能不能放雷射」(canFire)。
 * 機制(避免無腦長按):
 *  - 持續放(firing=true 且未過熱):heat += heatPerSec*dt;到 maxHeat → overheated=true。
 *  - 過熱中:強制不放、heat -= coolPerSec*dt;降到 resumeHeat 以下 → overheated=false。
 *  - 沒放:heat -= coolPerSec*dt(散熱)。
 * 純函式,不碰 scene;laser behavior 用回傳的 canFire 決定是否 fireLaser。
 */
export function advanceHeat(
  state: HeatState,
  dtSec: number,
  firing: boolean,
  p: HeatParams,
): { heat: number; overheated: boolean; canFire: boolean } {
  const dt = Math.max(0, dtSec);
  let heat = state.heat;
  let overheated = state.overheated;

  if (overheated) {
    // 過熱:強制散熱,不放,降夠了才解鎖。
    heat = Math.max(0, heat - p.coolPerSec * dt);
    if (heat <= p.resumeHeat) overheated = false;
    return { heat, overheated, canFire: false };
  }

  if (firing) {
    heat = Math.min(p.maxHeat, heat + p.heatPerSec * dt);
    if (heat >= p.maxHeat) {
      heat = p.maxHeat;
      overheated = true;
      return { heat, overheated, canFire: false }; // 剛見頂這幀就停
    }
    return { heat, overheated, canFire: true };
  }

  // 沒放:散熱。
  heat = Math.max(0, heat - p.coolPerSec * dt);
  return { heat, overheated, canFire: false };
}

/** 熱量比(0..1),給 UI 過熱條。 */
export function heatRatio(heat: number, maxHeat: number): number {
  if (maxHeat <= 0) return 0;
  const r = heat / maxHeat;
  return r < 0 ? 0 : r > 1 ? 1 : r;
}

/**
 * 雷射光束命中:活敵人「中心 y 距 beamCenterY」在 ±halfHeight 內即被掃到。
 * 回傳被命中的目標 index 清單(scene 據此每幀施低傷)。玩家上下移動 = 移動 beamCenterY
 * = 掃過不同 lane。純函式;只看 y(光束橫貫全寬,x 不限)。
 */
export function laserHitIndices(
  targets: readonly WeaponTargetInfo[],
  beamCenterY: number,
  halfHeight: number,
): number[] {
  const hits: number[] = [];
  for (let i = 0; i < targets.length; i++) {
    const t = targets[i];
    if (!t.alive) continue;
    if (Math.abs(t.y - beamCenterY) <= halfHeight) hits.push(i);
  }
  return hits;
}

// ── 榴彈炮(mortar)拋物 + AoE 純邏輯 ──────────────────────────────────────────

/**
 * 榴彈落點 x:從砲口 (originX) 朝前(右)拋,水平初速 muzzleSpeed、飛行 flightSec 秒,
 * 落在 originX + muzzleSpeed*flightSec。clamp 在 [originX, maxX] 內(不飛出戰場右緣)。
 * 「預判潑群」=玩家挑時機讓落點壓到敵群。純函式。
 */
export function mortarLandingX(
  originX: number,
  muzzleSpeed: number,
  flightSec: number,
  maxX: number,
): number {
  const raw = originX + Math.max(0, muzzleSpeed) * Math.max(0, flightSec);
  return Math.min(Math.max(raw, originX), maxX);
}

/**
 * 拋物線高度 offset:t∈[0,1] 飛行進度,回「相對直線的上拱位移」(負=往上,像素)。
 * 用 4·peak·t·(1-t)(標準拋物,t=0.5 時最高 = -peak)。scene 畫軌跡/砲彈 y 用。純函式。
 */
export function mortarArcOffsetY(t: number, peak: number): number {
  const tt = t < 0 ? 0 : t > 1 ? 1 : t;
  return -(4 * peak * tt * (1 - tt));
}

/**
 * AoE 圓形命中:落點 (cx,cy) 半徑 radius 內所有活敵人 index。回傳清單(scene 全施同傷)。
 * 「潑群」=一發涵蓋圈內全部。純函式。
 */
export function aoeHitIndices(
  targets: readonly WeaponTargetInfo[],
  cx: number,
  cy: number,
  radius: number,
): number[] {
  const hits: number[] = [];
  const r2 = radius * radius;
  for (let i = 0; i < targets.length; i++) {
    const t = targets[i];
    if (!t.alive) continue;
    const dx = t.x - cx;
    const dy = t.y - cy;
    if (dx * dx + dy * dy <= r2) hits.push(i);
  }
  return hits;
}
